<template>
    <transition name="fade" mode="out-in">
        <loader v-if="loading"></loader>
        <form v-else @submit.prevent="submitForm" class="quotetool__step">
            <div class="container">
                <h2 class="quotetool__step-heading">{{ labels.stepTwoTitle }}</h2>
                <div class="quotetool__step-form-container">
                    <transition name="fade">
                        <p class="error" v-if="error">{{ error }}</p>
                    </transition>

                    <!-- Water site count -->
                    <div class="form-group" :class="{ 'form-group--error': $v.waterSitesCount.$error }">
                        <label for="sitesCount">{{ labels.stepTwoNumberOfSitesLabel }}*</label>
                        <div class="row">
                            <div class="col-xs-12 col-sm-4">
                                <div class="custom-select">
                                    <select v-model="waterSitesCount" id="sitesCount" class="form-control">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">More than 5</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="error" v-if="!$v.waterSitesCount.required">Site count is required</div>

                    <!-- Notice message -->
                    <p v-html="labels.stepTwoSiteHelpText">

                    </p>
                    <hr />
                    <!-- Sites -->
                    <div v-if="waterSitesCount < 6">
                        <water-site v-for="(site, index) in waterSites"
                                    :key="index"
                                    :index="index"
                                    :value="site"
                                    @input="updateSiteDetails"
                                    :has-reference-number="hasReferenceNumber"
                                    :ref="'site' + index"></water-site>
                    </div>

                    <div v-if="continueQuoteToolJourney">
                        <!-- Custom billing address -->
                        <div class="checkbox">
                            <label>
                                <input v-model="useCustomBillingAddress" type="checkbox" :value="true">
                                {{ labels.stepTwoUseCustomBillingCheckboxLabel }}
                                <span class="checkmark"></span>
                            </label>
                        </div>

                        <!-- Postcode lookup component -->
                        <div v-if="useCustomBillingAddress">
                            <postcode-lookup v-model="customBillingAddress" ref="postcodeLookup"></postcode-lookup>
                        </div>

                        <!-- Acceptance -->
                        <div class="checkbox" :class="{ 'form-group--error': $v.confirmationReceived.$error }">
                            <label>
                                <input v-model="confirmationReceived" type="checkbox" :value="true">
                                {{ labels.stepTwoConfirmBillingLabel }}*
                                <span class="checkmark"></span>
                            </label>
                        </div>

                        <div class="error" v-if="!$v.confirmationReceived.required">Confirmation is required</div>
                        <div class="error" v-if="!$v.confirmationReceived.isTrue">Confirmation is required</div>
                    </div>

                    <button class="btn--chevron" @click.prevent="submitForm">{{ submitButtonText }}</button>

                </div>
            </div>
             
            <!--|| 3 col footer section--light-blue ||-->
            <section class="section--light-blue">
                <div class="container container--3-col">
                    <div class="row">
                        <div class="col-xs-12 text-center">
                            <h2>{{ labels.stepTwoUSPHeading }}</h2>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-xs-12 col-md-4">
                            <div class="container--3-col__col-wrapper">
                                <img src="/dist/img/drop-1.png" />
                                <p v-html="labels.stepTwoUSPOne"></p>
                            </div>
                        </div>
                        <div class="col-xs-12 col-md-4">
                            <div class="container--3-col__col-wrapper">
                                <img src="/dist/img/drop-2.png" />
                                <p v-html="labels.stepTwoUSPTwo"></p>
                            </div>
                        </div>
                        <div class="col-xs-12 col-md-4">
                            <div class="container--3-col__col-wrapper">
                                <img src="/dist/img/drop-3.png" />
                                <p v-html="labels.stepTwoUSPThree"></p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </form>
    </transition>
</template>

<script>
    import { mapMutations, mapActions, mapGetters, mapState } from 'vuex';
    import { createHelpers } from 'vuex-map-fields';
    import { required } from 'vuelidate/lib/validators';

    import StepNavigation from '../StepNavigation.vue';
    import PostcodeLookup from '../PostcodeLookup.vue';
    import WaterSite from '../WaterSite.vue';
    import Loader from '../Loader.vue';

    const { mapFields } = createHelpers({
        getterType: 'waterConsumptionStep/getField',
        mutationType: 'waterConsumptionStep/updateField',
    }); 

    export default {
        components: {
            StepNavigation,
            PostcodeLookup,
            WaterSite,
            Loader,
        },

        data() {
            return {
                loading: false,
                error: null,
            }
        },

        computed: {
            ...mapFields([
                'waterSitesCount',
                'waterSites',
                'useCustomBillingAddress',
                'confirmationReceived',
                'customBillingAddress',
            ]),

            ...mapState('microcopy', [
                'labels',
            ]),

            ...mapGetters('informationStep', [
                'hasReferenceNumber'
            ]),

            ...mapGetters('waterConsumptionStep', [
                'continueQuoteToolJourney'
            ]),

            submitButtonText() {
                return (this.continueQuoteToolJourney) ? this.labels.stepTwoRequestQuoteButtonText : 'Continue';
            }
        },

        methods: {
            ...mapMutations('waterConsumptionStep', [
                'addWaterSites',
                'removeWaterSites',
                'updateSite',
                'resetSitesBilling',
                'resetCustomBillingAddress'
            ]),

            ...mapActions('waterConsumptionStep', [
                'fetchSPID',
                'getQuotes',
            ]),

            ...mapActions('stepProgress', [
                'toggleDropoutConfirmationStep'
            ]), 
             
            updateSiteDetails(siteDetails) {
                if (siteDetails.site.isBillingSite == true) {
                    this.resetSitesBilling();
                    this.resetCustomBillingAddress();
                }
                this.updateSite(siteDetails);
            },

            submitForm() {
                if (!this.continueQuoteToolJourney) {
                    this.loading = true;
                    this.toggleDropoutConfirmationStep().then(() => {
                        this.loading = false;
                    });
                } else {
                    this.$v.$touch(); 

                    let isValidForm = !this.$v.$invalid;
                 
                    if (this.useCustomBillingAddress) {
                        this.$refs['postcodeLookup'].$v.$touch();

                        isValidForm = !this.$v.$invalid && !this.$refs['postcodeLookup'].$v.$invalid; 
                    }

                    for (let siteIndex = 0; siteIndex < this.waterSitesCount; siteIndex++) {
                        this.$refs[`site${siteIndex}`][0].$v.$touch();

                        if (this.$refs[`site${siteIndex}`][0].$v.$invalid) {
                            isValidForm = false;
                        }
                    }
                
                    if (isValidForm) {
                        this.loading = true;
                        this.getQuotes().then(data => {
                            this.loading = false;
                        }).catch(error => {
                            if (error == 'ADDRESS_MISSING') {
                                this.error = this.labels.stepTwoNoAddressValidationMessage;
                            } else {
                                this.error = this.labels.stepTwoCalculationValidationMessage;
                            }
                            this.loading = false;
                        });
                    }
                }

            }
        },

        watch: {
            waterSitesCount(newCount, oldCount) {
                if (newCount > oldCount) {
                    this.addWaterSites(newCount - oldCount);
                } else {
                    this.removeWaterSites(oldCount - newCount);
                }

            },

            useCustomBillingAddress(newValue, oldValue) {
                if (newValue == true) {
                    this.resetSitesBilling();
                }
            }
        },

        validations: {
            waterSitesCount: {
                required,
            },

            confirmationReceived: {
                required,
                isTrue: (value) => {
                    return value == true;
                }
            }
        }
    }
</script>